import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';

// Low-friction RAID capture, embedded where PMs already work (Delivery Cockpit,
// Project Update, Project Health). Shows the project's open RAID as colour-coded
// chips (× to close) and a single-line add: pick R/A/I/D, type a title, Enter.
// Only title + type are required — full scoring/detail stays optional in the
// RAID Register. When a milestoneId is given, new items are tagged to it.
const TYPES = [
  { v: 'risk',       letter: 'R', on: 'bg-red-600 text-white border-red-600',       chip: 'bg-red-50 text-red-700 border-red-200' },
  { v: 'assumption', letter: 'A', on: 'bg-blue-600 text-white border-blue-600',     chip: 'bg-blue-50 text-blue-700 border-blue-200' },
  { v: 'issue',      letter: 'I', on: 'bg-amber-500 text-amber-950 border-amber-500',   chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  { v: 'dependency', letter: 'D', on: 'bg-purple-600 text-white border-purple-600', chip: 'bg-purple-50 text-purple-700 border-purple-200' },
];
const letterOf = (v) => TYPES.find(t => t.v === v) || TYPES[0];

export default function RaidQuickPanel({ sowId, milestoneId = null, title = 'RAID' }) {
  const [items, setItems] = useState([]);
  const [type, setType]   = useState('risk');
  const [text, setText]   = useState('');
  const [busy, setBusy]   = useState(false);

  const load = useCallback(() => {
    if (!sowId) { setItems([]); return; }
    api.get('risks', { sow_id: sowId })
      .then(r => setItems((r || []).filter(x => x.status !== 'closed')))
      .catch(() => setItems([]));
  }, [sowId]);
  useEffect(() => { load(); }, [load]);

  // Refresh when the user returns to this tab, or when the full RAID register
  // (which may be open in another tab) reports a change for this project — so a
  // PM who opens the register, edits, and comes back sees the latest without a
  // manual reload.
  useEffect(() => {
    const onFocus = () => { if (!document.hidden) load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    let ch;
    try {
      ch = new BroadcastChannel('av:raid');
      ch.onmessage = (e) => {
        const changed = e?.data?.sow_id;
        if (!changed || String(changed) === String(sowId)) load();
      };
    } catch { /* BroadcastChannel unsupported — focus refresh still covers it */ }
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      if (ch) ch.close();
    };
  }, [load, sowId]);

  const add = async () => {
    const t = text.trim();
    if (!t || !sowId || busy) return;
    setBusy(true);
    try {
      await api.post('risks', {
        raid_type: type, title: t, sow_id: sowId, status: 'open',
        categories: ['delivery'], milestone_ids: milestoneId ? [String(milestoneId)] : [],
        // One-line capture — flag it so the SOW's PM gets a to-do to fill in the
        // full detail, and it shows a ⚠ until they do.
        pre_staged: 1,
      });
      setText(''); load();
    } finally { setBusy(false); }
  };
  const close = async (id) => { await api.delete(`risks/${id}`).catch(() => {}); load(); };

  // When scoped to a milestone, only show items tagged to it.
  const shown = milestoneId
    ? items.filter(i => { try { return (JSON.parse(i.milestone_ids || '[]') || []).map(String).includes(String(milestoneId)); } catch { return false; } })
    : items;

  return (
    <div>
      {(title || milestoneId) && (
        <div className="flex items-center justify-between mb-1">
          <span className="text-[8px] font-bold uppercase text-gray-500">{title}</span>
          {milestoneId && <span className="text-[7px] text-gray-400">tagged to this milestone</span>}
        </div>
      )}
      <div className="flex flex-wrap gap-1 mb-1.5">
        {shown.length === 0 && <span className="text-[8px] text-gray-400 italic">None yet — add below.</span>}
        {shown.map(i => {
          const m = letterOf(i.raid_type || 'risk');
          return (
            <span key={i.id} className={`inline-flex items-center gap-1 text-[8px] font-semibold px-1.5 py-0.5 rounded border ${m.chip}`} title={i.description || i.reference || ''}>
              <span className="font-extrabold">{m.letter}</span>{i.title}
              {i.pre_staged ? <span className="text-amber-500" title="Pre-staged — awaiting PM expansion of details">⚠</span> : null}
              <button type="button" onClick={() => close(i.id)} title="Close this item" className="text-gray-400 hover:text-red-500 ml-0.5 leading-none">×</button>
            </span>
          );
        })}
      </div>
      <div className="flex items-center gap-1">
        <div className="flex gap-0.5 flex-shrink-0">
          {TYPES.map(t => (
            <button key={t.v} type="button" onClick={() => setType(t.v)} title={t.v}
              className={`w-5 h-5 text-[9px] font-extrabold rounded border transition-colors ${type === t.v ? t.on : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300'}`}>{t.letter}</button>
          ))}
        </div>
        <input value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Type it and press Enter…" className="flex-1 min-w-0 form-input text-[9px] py-1" />
        <button type="button" onClick={add} disabled={busy || !text.trim()} className="px-2 py-1 text-[9px] font-bold bg-navy text-white rounded disabled:opacity-40 flex-shrink-0">＋</button>
      </div>
    </div>
  );
}
